import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import SearchableSelect from "../../components/SearchableSelect";
import { api } from "../../lib/api";
import { labelFromCode, statusTone } from "../shared/format";

export default function OrganizationConnections({ organizationId, canEdit, organizationActive, active }: {
  organizationId: string; canEdit: boolean; organizationActive: boolean; active: boolean;
}) {
  const cache = useQueryClient();
  const [selected, setSelected] = useState("");
  const query = useQuery({ queryKey: ["organization-connections", organizationId],
    queryFn: () => api.getOrganizationConnections(organizationId), enabled: active });
  const mutation = useMutation({
    mutationFn: () => api.createAdminConnection({ organization_id: organizationId, connected_organization_id: selected }),
    onSuccess: async () => {
      setSelected("");
      await Promise.all([
        cache.invalidateQueries({ queryKey: ["organization-connections"] }),
        cache.invalidateQueries({ queryKey: ["ops-crm-discovery"] }),
        cache.invalidateQueries({ queryKey: ["tech-admin-audit"] })
      ]);
    },
    onError: () => { void cache.invalidateQueries({ queryKey: ["organization-connections"] }); }
  });
  const canAdd = organizationActive && !mutation.isPending && query.data?.available.some(org => org.organization_id === selected);
  return <section className="pa-panel pa-detail-padding pa-page-stack pa-organization-connections">
    <div><h2>Connected organizations</h2><p>Only active organizations in this list are available when importing customers or vendors from Coprint. Connections are mutual and managed only by ops.</p></div>
    {query.isPending ? <p role="status">Loading connections…</p> : query.isError ?
      <p className="pa-form-error" role="alert">Could not load connections. <button type="button" className="pa-secondary-button" onClick={() => void query.refetch()}>Retry</button></p> : <>
      {query.data.connected.length ? <div className="pa-table-scroll"><table className="pa-table">
        <thead><tr><th>Organization</th><th>Type</th><th>Status</th></tr></thead>
        <tbody>{query.data.connected.map(org => <tr key={org.organization_id}>
          <td><Link className="pa-detail-link" to={`/organizations/${org.organization_id}?tab=connections`}>{org.organization_name}</Link></td>
          <td>{org.organization_type === "SMB" ? "SMB" : labelFromCode(org.organization_type)}</td>
          <td><span className={`pa-badge ${statusTone(org.status)}`}>{labelFromCode(org.status)}</span></td>
        </tr>)}</tbody>
      </table></div> : <p>No connected organizations yet.</p>}
      {!organizationActive && <p className="pa-form-note">Activate this organization before adding connections. Only active, unblocked organizations are available for import.</p>}
      {canEdit ? <form className="pa-admin-form" onSubmit={event => { event.preventDefault(); if (canAdd) mutation.mutate(); }}>
        <div className="pa-crm-organization-choice"><span>Organization</span>
          <SearchableSelect label="Organization to connect" value={selected} onChange={value => { setSelected(value); mutation.reset(); }}
            options={query.data.available.map(org => ({ value: org.organization_id, label: org.organization_name,
              description: org.organization_type === "SMB" ? "SMB" : labelFromCode(org.organization_type) }))}
            disabled={!organizationActive || mutation.isPending || !query.data.available.length} placeholder="Select an organization" />
        </div>
        {!query.data.available.length && <p className="pa-form-note">No additional organizations are available to connect.</p>}
        {mutation.isError && <p className="pa-form-error" role="alert">{mutation.error instanceof Error ? mutation.error.message : "Could not add connection."}</p>}
        {mutation.isSuccess && <p className="pa-form-success" role="status">Connection added.</p>}
        <div className="pa-detail-actions"><button type="submit" className="pa-primary-button"
          disabled={!canAdd}>
          {mutation.isPending ? "Adding…" : "Add new connection"}
        </button></div>
      </form> : <p className="pa-form-note">Tech Admin access is required to add connections.</p>}
    </>}
  </section>;
}
